import { z } from 'zod'
import { coverageRuleInputSchema, coverageRuleSchema } from './coverage'

/**
 * The public API's endpoints and keys (clipbook-api), as the internal admin routes under
 * `/api/api-access` create and list them. A key's secret appears exactly once, in the response that
 * creates it (`CreatedApiKey.secret`); nothing else ever carries it or its hash.
 */

/**
 * Where clipbook-api answers: REST under `/v1/*`, MCP (Streamable HTTP) at `/mcp`, a key as
 * `Authorization: Bearer cb_live_…`. The Cloud Run hostname until `api.clipbook.io` (the host its
 * OpenAPI document names) is mapped; the admin page's usage examples are built from it.
 */
export const CLIPBOOK_API_BASE_URL = 'https://clipbook-api-wsekcl7eqq-uc.a.run.app'

/**
 * The API's source names (clipbook-api src/lib/sources.ts), in its order; an endpoint serves a subset.
 * `broadcast` is TV and radio clips: the topics' broadcast queries, registered with the clip vendor
 * by tveyes-sync, served from the clips clipbook-ingest copies.
 */
export const API_SOURCES = [
  'news',
  'x',
  'linkedin',
  'newsletter',
  'podcast',
  'broadcast',
  'scholarly',
] as const
export const API_ENDPOINT_STATUSES = ['active', 'paused', 'deactivated'] as const

const limits = {
  pageSize: z.number().int().min(1).max(1000),
  rateLimitPerMinute: z.number().int().min(1).max(10000),
  monthlyQuota: z.number().int().min(0),
  lookbackDays: z.number().int().min(1).max(3650),
}

export const createApiEndpointSchema = z.object({
  name: z.string().trim().min(1).max(200),
  /**
   * The workspace (sub-account) the endpoint is made in, one endpoint each; its people manage the
   * keys, and its topics come from the workspace's account. Fixed once made.
   */
  subaccountId: z.uuid(),
  topicIds: z.array(z.uuid()).min(1).max(200),
  sources: z.array(z.enum(API_SOURCES)).min(1),
  pageSize: limits.pageSize.optional(),
  rateLimitPerMinute: limits.rateLimitPerMinute.optional(),
  monthlyQuota: limits.monthlyQuota.optional(),
  lookbackDays: limits.lookbackDays.optional(),
})
export type CreateApiEndpoint = z.infer<typeof createApiEndpointSchema>

/**
 * The coverage an endpoint applies: Headline coverage lists of outlets (live: an edit to the list moves
 * the endpoint) and single domains, as `outlets.exclude` rules — the only field clipbook-api and its
 * relevance judge read (`coverage_rules.api_endpoint_id`). An article from one is never served.
 */
const apiEndpointCoverageRuleSchema = coverageRuleInputSchema.refine(
  (rule) => rule.field === 'outlets.exclude',
  { message: 'An API endpoint takes excluded outlets only (outlets.exclude).' },
)

export const updateApiEndpointSchema = createApiEndpointSchema
  .omit({ subaccountId: true })
  .partial()
  .extend({
    status: z.enum(API_ENDPOINT_STATUSES).optional(),
    /** Full replacement of the endpoint's excluded outlets. */
    coverage: z.array(apiEndpointCoverageRuleSchema).max(200).optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to change' })
export type UpdateApiEndpoint = z.infer<typeof updateApiEndpointSchema>

export const createApiKeySchema = z.object({
  name: z.string().trim().min(1).max(200),
  expiresAt: z.iso.datetime().optional(),
})
export type CreateApiKey = z.infer<typeof createApiKeySchema>

/**
 * A key made by a Clipbook admin (`/api/api-access`), who says which kind it is: `internal` is Clipbook's
 * own key on the client's endpoint (Kevin 2026-11-03) — clipbook-api records its calls but never bills them
 * and keeps it off the endpoint's shared per-minute meter. Set only here, when the key is made; a
 * workspace's people make client keys only (`createApiKeySchema`).
 */
export const createAdminApiKeySchema = createApiKeySchema.extend({ internal: z.boolean() })
export type CreateAdminApiKey = z.infer<typeof createAdminApiKeySchema>

export const apiKeySchema = z.object({
  id: z.uuid(),
  endpointId: z.uuid(),
  name: z.string(),
  /** Identifies the key (`cb_live_<prefix>_…`); not secret. */
  prefix: z.string(),
  createdBy: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  /** Clipbook's own key on the endpoint (`createAdminApiKeySchema`): never billed, never the client's. */
  internal: z.boolean(),
})
export type ApiKey = z.infer<typeof apiKeySchema>

/** A key with who created it, by name (or address); null once they are gone. */
const ownedApiKeySchema = apiKeySchema.extend({ owner: z.string().nullable() })

export const apiEndpointSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /** Null only for an endpoint made before endpoints were made in a workspace that migration 0070
   *  could not place (its topics span accounts). */
  subaccountId: z.uuid().nullable(),
  status: z.enum(API_ENDPOINT_STATUSES),
  topicIds: z.array(z.uuid()),
  sources: z.array(z.string()),
  pageSize: z.number().int(),
  rateLimitPerMinute: z.number().int(),
  monthlyQuota: z.number().int(),
  lookbackDays: z.number().int(),
  createdAt: z.iso.datetime(),
  /** Every key, client and internal (`internal`), each with its owner. */
  keys: z.array(ownedApiKeySchema),
  /** The outlets the endpoint never serves (`outlets.exclude` rules). */
  coverage: z.array(coverageRuleSchema),
})
export type ApiEndpoint = z.infer<typeof apiEndpointSchema>

export const createdApiKeySchema = z.object({
  key: apiKeySchema,
  /** `cb_live_<prefix>_<secret>`: shown once, stored nowhere. */
  secret: z.string(),
})
export type CreatedApiKey = z.infer<typeof createdApiKeySchema>

/**
 * When a key stops working, chosen each time one is revoked or rotated away: at once, or after a grace
 * period so whoever holds it can switch over. "now" revokes the key (`revoked_at`); a grace period
 * brings its expiry forward (`expires_at`, never later than one it already had), because clipbook-api
 * refuses any key with a `revoked_at`, past or future, and refuses an expired one by the clock.
 */
export const KEY_RETIREMENTS = ['now', '1h', '24h'] as const
export type KeyRetirement = (typeof KEY_RETIREMENTS)[number]

export const KEY_RETIREMENT_LABELS: Record<KeyRetirement, string> = {
  now: 'Immediately',
  '1h': 'In 1 hour',
  '24h': 'In 24 hours',
}

const KEY_RETIREMENT_MS: Record<Exclude<KeyRetirement, 'now'>, number> = {
  '1h': 3_600_000,
  '24h': 86_400_000,
}

/** The moment a grace period ends, or null for "now". */
export function keyRetiresAt(retirement: KeyRetirement, now: Date): Date | null {
  return retirement === 'now' ? null : new Date(now.getTime() + KEY_RETIREMENT_MS[retirement])
}

export const retireApiKeySchema = z.object({ retire: z.enum(KEY_RETIREMENTS).default('now') })
export type RetireApiKey = z.infer<typeof retireApiKeySchema>

/** A rotation: a new key under the same name, and when the old one stops working. */
export const rotatedApiKeySchema = createdApiKeySchema.extend({ retired: apiKeySchema })
export type RotatedApiKey = z.infer<typeof rotatedApiKeySchema>

/**
 * A workspace's API product, as its own people see it: its endpoint's status and limits (for the
 * usage callouts) and its keys. What the endpoint reads — its topics, its excluded outlets — is
 * Clipbook's configuration, read on the admin routes, never here; so are Clipbook's internal keys on
 * the endpoint: `keys` are the client's own. `endpoint` is null until Clipbook sets one up.
 */
export const workspaceApiSchema = z.object({
  baseUrl: z.string(),
  endpoint: apiEndpointSchema
    .omit({ topicIds: true, coverage: true })
    .extend({
      /** The client's keys (never an internal one), each with its owner. */
      keys: z.array(ownedApiKeySchema),
    })
    .nullable(),
})
export type WorkspaceApi = z.infer<typeof workspaceApiSchema>

/**
 * Clipbook's API page across every account: each workspace with an endpoint, and each account that
 * bought API & MCP with none yet (its own space), so a click opens the workspace's own page. An
 * endpoint no workspace holds is listed apart, never guessed into one.
 */
const apiEndpointSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: z.enum(API_ENDPOINT_STATUSES),
  topicCount: z.number().int(),
  activeKeys: z.number().int(),
  keys: z.number().int(),
})

export const apiOverviewSchema = z.object({
  workspaces: z.array(
    z.object({
      accountId: z.uuid(),
      accountName: z.string(),
      subaccountId: z.uuid(),
      subaccountName: z.string(),
      isPrimary: z.boolean(),
      /** The account has `delivery:api`; without it the workspace's page is not offered. */
      entitled: z.boolean(),
      endpoint: apiEndpointSummarySchema.nullable(),
    }),
  ),
  unplaced: z.array(apiEndpointSummarySchema),
})
export type ApiOverview = z.infer<typeof apiOverviewSchema>
